// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import { CaretUp } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

import {
  AskPunttReviewCommentBadge,
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  type AssetFile,
  formatReviewCommentTime,
  reviewCommentAuthorLabel,
  reviewCommentCollapsedSections,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";

type AskV2ReviewCommentsListProps = {
  comments: readonly AskPunttReviewComment[];
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  onSelect: (commentId: string) => void;
  onClear: () => void;
};

function CommentSeverityBadge({
  severity,
}: {
  severity: AskPunttReviewComment["severity"];
}) {
  const { t } = useTranslation("ui");
  if (!severity) return null;

  const label =
    severity === "high"
      ? t("highConfidence")
      : severity === "medium"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-puntt-neutral-gray-3 px-2 py-0.5 text-[11px] font-medium text-puntt-neutral-gray-11"
      data-testid="ask-v2-review-comment-confidence-badge"
    >
      <CaretUp size={10} weight="bold" aria-hidden="true" />
      {label}
    </span>
  );
}

function CollapsedCommentPreview({
  comment,
}: {
  comment: AskPunttReviewComment;
}) {
  const sections = reviewCommentCollapsedSections(comment.description);
  if (sections.length === 0) {
    const preview = comment.description.trim().split("\n").slice(1).join(" ");
    if (!preview) return null;
    return (
      <p className="mt-2 text-[12px] leading-relaxed text-puntt-neutral-gray-10">
        {preview}
      </p>
    );
  }

  return (
    <div
      className="mt-2 space-y-2"
      data-testid={`ask-v2-review-comment-preview-${comment._id}`}
    >
      {sections.map((section) => (
        <p
          key={section.label}
          className="text-[12px] leading-relaxed text-puntt-neutral-gray-11"
          data-testid={`ask-v2-review-comment-section-${comment._id}-${section.label.toLowerCase()}`}
        >
          <span className="font-semibold text-puntt-neutral-gray-12">
            {section.label}:
          </span>{" "}
          {section.body}
        </p>
      ))}
    </div>
  );
}

export function AskV2ReviewCommentsList({
  comments,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  onSelect,
  onClear,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const visibleComments = comments.filter((comment) => !comment.isDeleted);
  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <h2
        className="px-4 py-3 text-[13px] font-bold text-puntt-neutral-gray-12"
        data-testid="ask-v2-review-findings-title"
      >
        {t("askV2.review.findingsTitle")}
      </h2>
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
      >
        {visibleComments.map((comment, index) => {
          const id = comment._id;
          const expanded = selectedCommentId === id;
          const title = reviewCommentTitle(comment);
          const commentNumber = index + 1;

          return (
            <li key={id} className="mb-2">
              <div
                className="overflow-hidden rounded-lg border border-puntt-neutral-gray-5 bg-base-white"
                data-testid={`ask-v2-review-comment-card-${id}`}
              >
                <button
                  type="button"
                  className="w-full bg-base-white p-3 text-left"
                  data-testid={`ask-v2-review-comment-${id}`}
                  aria-expanded={expanded}
                  onClick={() => (expanded ? onClear() : onSelect(id))}
                >
                  <div className="flex items-start gap-3">
                    <AskPunttReviewCommentBadge
                      number={commentNumber}
                      data-testid={`ask-v2-review-comment-index-${id}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className="text-[13px] font-semibold leading-snug text-puntt-neutral-gray-12"
                        data-testid={`ask-v2-review-comment-title-${id}`}
                      >
                        {title}
                      </p>
                      {expanded ? (
                        <>
                          <div
                            className="mt-1 flex min-w-0 items-center gap-2 text-[12px] text-puntt-neutral-gray-9"
                            data-testid={`ask-v2-review-comment-author-${id}`}
                          >
                            <span className="font-medium text-puntt-neutral-gray-11">
                              {reviewCommentAuthorLabel(comment, authorOptions)}
                            </span>
                            <span>
                              {formatReviewCommentTime(comment.createdAt)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {comment.isRequired ? (
                              <span
                                className="rounded-full bg-puntt-red-9 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-base-white"
                                data-testid="ask-v2-review-comment-urgent-badge"
                              >
                                {t("askV2.review.urgentBadge")}
                              </span>
                            ) : null}
                            <CommentSeverityBadge severity={comment.severity} />
                          </div>
                        </>
                      ) : (
                        <CollapsedCommentPreview comment={comment} />
                      )}
                    </div>
                  </div>
                </button>
                {expanded ? (
                  <div
                    className="bg-base-white px-3 pb-3 pt-0"
                    data-testid={`ask-v2-review-comment-expanded-${id}`}
                  >
                    <AskPunttReviewCommentBody
                      commentId={id}
                      description={comment.description}
                      afterExpand={
                        <AskPunttReviewCommentSource
                          comment={comment}
                          contextFiles={contextFiles}
                          reviewFiles={reviewFiles}
                        />
                      }
                    />
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
